"use client";
import { Box, Card, Stack, Typography, useMediaQuery } from "@mui/material";
import { FC, memo } from "react";
import { OrderCardItem, OrderCardProps } from "../lib/type";
import { WebShopIcon } from "../../WebShopIcon";
import useToggle from "@/src/hooks/useToggle";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { extractNames, ParsedValue } from "../../TextWithValue";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { useFormContext } from "react-hook-form";

const OrderCardItemField: React.FC<OrderCardItem> = memo(
  function UnmemoizedOrderCardItemField({
    price = "",
    name,
    hideIfEmpty,
    maskType,
  }) {
    const { getValues } = useFormContext();
    const names = extractNames(price);
    const isHidden = hideIfEmpty && names.some((name) => !getValues(name));

    return (
      !isHidden && (
        <Stack
          className="justify-between flex-center-row"
          key={`${name}-${price}`}
        >
          <Typography variant="body1">{name}</Typography>
          <Typography variant="body1">
            <ParsedValue value={price} maskType={maskType} />
          </Typography>
        </Stack>
      )
    );
  },
);

export function OrderCard({
  items,
  totalPrice = "",
  maskType,
  // mobile = false,
  className,
  ...rest
}: OrderCardProps) {
  // const screenWidth = window.innerWidth;
  // const isMobile = screenWidth < 768;
  const isMobile = useMediaQuery("(max-width:768px)");

  const { isToggled, toggle } = useToggle({ initialState: !isMobile });
  const orderNumber = useProcessInstanceStore(
    ({ processInstance }) => processInstance?.order?.number || "",
  );

  return (
    <Card
      className={cn(
        "triangles-bottom w-full select-none overflow-visible rounded-b-none p-4",
        { "after:content-none": !isToggled },
        !isMobile && "max-w-72",
        className,
      )}
      {...rest}
    >
      <Box className="flex w-full flex-col gap-4">
        {orderNumber && (
          <Stack
            className="cursor-pointer justify-between gap-2 flex-center-row"
            onClick={toggle}
          >
            <Typography
              className="text-lg font-semibold text-dark-blue"
              component={"h4"}
            >
              Ваше замовлення: №{orderNumber}
            </Typography>
            <Box
              className="flex transition-transform"
              style={{
                transform: !isToggled ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <WebShopIcon
                name="arrowLeft"
                className="fill-blue"
                style={{ transform: "rotate(90deg)" }}
              />
            </Box>
          </Stack>
        )}
        {isToggled && (
          <>
            <div className="flex flex-col gap-2">
              {items?.map((item, i) => (
                <OrderCardItemField {...item} key={i} maskType={maskType} />
              ))}
            </div>
            <Box className="border-x-0 border-b-0 border-t border-dashed border-light-gray" />
            {totalPrice && (
              <Stack className="justify-between flex-center-row">
                <Typography className="text-lg font-semibold text-dark-blue">
                  Всього
                </Typography>
                <Typography className="text-lg font-semibold text-dark-blue">
                  <ParsedValue value={totalPrice} maskType={maskType} />
                </Typography>
              </Stack>
            )}
          </>
        )}
      </Box>
    </Card>
  );
}
